import React from 'react';
import { useBooleanState } from '@hqo/react-components-library/dist/hooks';
import {
  CardIcon,
  CardTitle,
  CompactIcon,
  CompactTitle,
  Container,
  Content,
  Header,
  Icon,
  SectionIcon,
  Title,
} from './styles';

export interface CollapsableBlockProps {
  title: string;
  children: React.ReactNode;
  // RES-999: see Container/Header/Content/CompactTitle in styles.ts — swaps this block's normal
  // bold heading + top border for a small grey label matching the booking-details card's other
  // rows. Defaults to false so every other caller (e.g. checkout add-ons) is unaffected.
  compact?: boolean;
  /*
   * RES-1316: a block that owns its own card. Keeps the bold heading of the default, but drops the
   * top border (the card already has one) and the 28px lead-in, and uses a caret sized to the text.
   */
  section?: boolean;
  /*
   * RES-1367: a block that IS a card, rather than a section inside one.
   *
   * Takes the card's own heading -- the same size and weight as the resource name and the
   * conversation's title -- and a muted caret to match. `section` stays what it was: a sub-heading
   * INSIDE a card (Amenities, Additional information), which has to sit below the card's title
   * rather than compete with it.
   */
  card?: boolean;
  /*
   * Starts open. Defaults false, so every existing caller keeps the collapsed-first behaviour; the
   * ordered add-ons opt in because a collapsed block there reads as the order being absent.
   */
  defaultExpanded?: boolean;
}

export const CollapsableBlock = ({
  title,
  children,
  compact = false,
  section = false,
  card = false,
  defaultExpanded = false,
}: CollapsableBlockProps): JSX.Element => {
  const { value: isInfoExpanded, toggle: toggleInfoExpanded } = useBooleanState(defaultExpanded);

  /* Space scrolls the page by default, which is not what pressing a heading should do. */
  const handleHeaderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleInfoExpanded();
    }
  };
  const headingComponent = card ? CardTitle : Title;
  const TitleComponent = compact ? CompactTitle : headingComponent;
  /* compact: small grey caret. card: muted, matching the pane module's. section: sized to a 16px heading. */
  const sizedIcon = (card && CardIcon) || (section && SectionIcon) || Icon;
  const IconComponent = compact ? CompactIcon : sizedIcon;

  return (
    <Container compact={compact} section={section || card}>
      {/*
        ⭐ THE WHOLE ROW IS THE CONTROL, and now it says so. It has always toggled on click; the role,
        the tab stop and the key handling are what make that reachable without a mouse, and
        `aria-expanded` is what tells a screen reader which way it is going to go.
      */}
      <Header
        onClick={toggleInfoExpanded}
        onKeyDown={handleHeaderKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isInfoExpanded}
        compact={compact}
        section={section || card}
      >
        <TitleComponent>{title}</TitleComponent>
        <IconComponent
          icon={['fal', isInfoExpanded ? 'angle-up' : 'angle-down']}
          size={compact || section ? undefined : '2x'}
        />
      </Header>
      {/* RES-999: compact mode skips rendering Content entirely while collapsed — it's a plain
          div with its own margin-top, so leaving it mounted (as the non-compact behavior does)
          left a dead ~16-28px gap below the header even with nothing expanded underneath it. */}
      {(!compact || isInfoExpanded) && (
        <Content compact={compact} section={section || card}>
          {isInfoExpanded && children}
        </Content>
      )}
    </Container>
  );
};
